import { FormArray, FormControl, FormGroup } from '@angular/forms';
import {
  BorderFoundation,
  BorderStyles,
  defaultFoundationTokenScales,
  FoundationGroup,
  FoundationScales,
  isFoundationTokenNameValid,
  slugifyPaletteToken,
  ThemeTokenOrder,
} from '@finxone-platform/shared/sys-config-types';
import { orderNames } from './global-theme-token-order.utils';

/** One editable row of a token table; the saved scale is keyed by `name`. */
export interface FoundationTokenRow {
  name: string;
  value: string;
  style?: BorderStyles;
}

/** One FormGroup per token; border tokens also carry a `style` control. */
export function buildFoundationTokenFormGroup(token: FoundationTokenRow, group: FoundationGroup): FormGroup {
  const controls: Record<string, FormControl<string>> = {
    name: new FormControl(token.name ?? '') as FormControl<string>,
    value: new FormControl(token.value ?? '') as FormControl<string>,
  };
  if (group === 'border') {
    controls['style'] = new FormControl(token.style ?? BorderStyles.SOLID) as FormControl<string>;
  }
  return new FormGroup(controls);
}

// Length in px for ordering; an unparseable value sorts last.
function tokenSize(value: string): number {
  const match = /^(\d*\.?\d+)(px|rem|em)?$/.exec(value ?? '');
  if (!match) return Number.POSITIVE_INFINITY;
  return Number(match[1]) * (match[2] === 'rem' || match[2] === 'em' ? 16 : 1);
}

/**
 * A saved scale as table rows, in the saved `order`; tokens it doesn't list
 * follow smallest first (jsonb doesn't keep key order).
 */
export function scaleToRows(
  group: FoundationGroup,
  scale: NonNullable<FoundationScales[FoundationGroup]>,
  order?: string[],
): FoundationTokenRow[] {
  const rows = Object.entries(scale).map(([name, token]) =>
    group === 'border' ? { name, ...(token as BorderFoundation) } : { name, value: token as string },
  );
  rows.sort((a, b) => tokenSize(a.value) - tokenSize(b.value));
  const names = orderNames(
    rows.map((row) => row.name),
    order,
  );
  return rows.sort((a, b) => names.indexOf(a.name) - names.indexOf(b.name));
}

const FOUNDATION_GROUPS: FoundationGroup[] = ['spacing', 'radius', 'border', 'iconSizes'];

/**
 * The `foundations` FormGroup: one FormArray of rows per scale, so rename,
 * add and delete stay row edits. A theme saved before a scale existed starts
 * from that scale's defaults.
 */
export function buildFoundationsFormGroup(
  foundations?: Partial<FoundationScales>,
  order?: ThemeTokenOrder['foundations'],
): FormGroup {
  const controls: Record<string, FormArray<FormGroup>> = {};
  for (const group of FOUNDATION_GROUPS) {
    const rows = scaleToRows(
      group,
      foundations?.[group] ?? defaultFoundationTokenScales[group] ?? {},
      order?.[group],
    );
    controls[group] = new FormArray(rows.map((row) => buildFoundationTokenFormGroup(row, group)));
  }
  return new FormGroup(controls);
}

/** The `foundations` form value in its saved, keyed-by-name shape. */
export function toFoundationTokenScales(
  formValue: Record<FoundationGroup, FoundationTokenRow[]>,
): FoundationScales {
  const scales = { spacing: {}, radius: {}, border: {}, iconSizes: {} } as FoundationScales;
  for (const group of FOUNDATION_GROUPS) {
    for (const { name, value, style } of formValue[group] ?? []) {
      (scales[group] as Record<string, unknown>)[name] =
        group === 'border' ? { value, style: style ?? BorderStyles.SOLID } : value;
    }
  }
  return scales;
}

function tokenNames(tokens: FormArray<FormGroup>): string[] {
  return tokens.controls.map((token) => token.get('name')?.value as string);
}

/**
 * Whether `name` can be used for the token at `index` in `tokens`: a valid,
 * non-reserved name whose CSS variable doesn't collide with a sibling's
 * ("Large" and "large" both produce `--space-large`).
 */
export function isFoundationTokenNameAvailable(
  tokens: FormArray<FormGroup>,
  group: FoundationGroup,
  name: string,
  index: number,
): boolean {
  if (!isFoundationTokenNameValid(group, name)) return false;
  const slug = slugifyPaletteToken(name);
  return !tokenNames(tokens).some((sibling, i) => i !== index && slugifyPaletteToken(sibling) === slug);
}

/** Appends a token under the scale's next free `Custom N` name. */
export function addFoundationToken(
  tokens: FormArray<FormGroup>,
  group: FoundationGroup,
  value: string,
): void {
  let index = 1;
  while (!isFoundationTokenNameAvailable(tokens, group, `Custom ${index}`, -1)) index++;
  tokens.push(buildFoundationTokenFormGroup({ name: `Custom ${index}`, value }, group));
}

export function removeFoundationToken(tokens: FormArray<FormGroup>, index: number): void {
  tokens.removeAt(index);
}

export function reorderFoundationToken(
  tokens: FormArray<FormGroup>,
  fromIndex: number,
  toIndex: number,
): void {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= tokens.length ||
    toIndex >= tokens.length
  ) {
    return;
  }
  const token = tokens.at(fromIndex);
  tokens.removeAt(fromIndex, { emitEvent: false });
  tokens.insert(toIndex, token);
}
